import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { selectSite } from '../../../store/authReducer';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import { API_REQUEST, showDialog } from '../../../core/actions';
import * as AssetsAPI from '../../../core/api/assets';
import { addInventoryItem, fetchInventoryTypes } from '../../inventory/actions';
import { getJwtToken } from '../../../core/storage';
import { TOKEN_HEADER } from '../../../core/constants';
import TaskTemplateAdminDrawer, {
  AssetScope,
  DrawerFormState,
} from '../../taskTemplates/components/TaskTemplateAdminDrawer';
import {
  selectAssetTemplates,
  selectAssetGroups,
  selectAssetItems,
  selectAssetSiteInventories,
  selectAssetTaskOccurrences,
  selectAssetTree,
  selectSelectedTemplateId,
  selectSelectedNodeId,
  selectExpandedNodeIds,
  selectAssetSearchTerm,
  selectAssetLoading,
  selectAssetError,
  selectAddTemplateDrawerOpen,
  selectNodeActionDrawer,
  selectSelectedSidebarItem,
} from '../reducer';
import {
  fetchAssetTemplates,
  fetchAssetGroups,
  fetchAssetItems,
  fetchAssetTaskOccurrences,
  fetchAssetSiteInventories,
  selectAssetNode,
  toggleTreeNode,
  setAssetSearchTerm,
  expandAllNodes,
  collapseAllNodes,
  openAddTemplateDrawer,
  closeAddTemplateDrawer,
  openNodeActionDrawer,
  closeNodeActionDrawer,
  deselectTemplate,
  resetAssetView,
  selectSidebarItem,
  deselectSidebarItem,
  fetchAssetGroupDetail,
  fetchAssetItemDetail,
  GET_ASSET_TEMPLATE,
} from '../actions';
import {
  findNodeById,
  getBreadcrumbPath,
  buildGroupNode,
  buildItemNode,
  getGroupDisplayName,
  getItemDisplayName,
} from '../helpers';
import AssetTree from '../components/AssetTree';
import AssetDetail from '../components/AssetDetail';
import AddTemplateDrawer from '../components/AddTemplateDrawer';
import AddToNodeDrawer from '../components/AddToNodeDrawer';
import ArchiveConfirmModal, { ArchiveTarget } from '../components/ArchiveConfirmModal';
import { Drawer } from '../../../core/components/Drawer';
import OccurrenceDrawerBody from '../../taskTemplates/components/OccurrenceDrawerBody';
import {
  TaskOccurrence,
  useUpdateOccurrenceMutation,
} from '../../../core/api/rtk/taskTemplatesApi';

const TASK_DRAWER_WIDTH = 60;

const inventoryScopeTemplateId = (node: any): number | null => {
  if (node?.type !== 'template') return null;
  const raw = node?.rawId ?? node?.raw?.id ?? node?.data?.id ?? null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const countDescendants = (node: any): number => {
  const children = node?.children || [];
  return children.reduce((sum: number, child: any) => sum + 1 + countDescendants(child), 0);
};

const AssetsContainer: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const site = useSelector(selectSite);
  const multiSiteSelection = useSelector(selectMultiSiteSelection);

  const templates = useSelector(selectAssetTemplates);
  const groups = useSelector(selectAssetGroups);
  const items = useSelector(selectAssetItems);
  const siteInventories = useSelector(selectAssetSiteInventories);
  const tree = useSelector(selectAssetTree);
  const assetTaskOccurrences = useSelector(selectAssetTaskOccurrences);
  const linkableTaskOccurrences = useMemo(
    () =>
      (assetTaskOccurrences || []).filter(
        (o: any) =>
          !o?.archivedAt &&
          o?.assetGroupId == null &&
          o?.assetItemId == null &&
          o?.assetTemplateId == null &&
          o?.completedAt == null
      ),
    [assetTaskOccurrences]
  );

  const selectedTemplateId = useSelector(selectSelectedTemplateId);
  const selectedNodeId = useSelector(selectSelectedNodeId);
  const expandedNodeIds = useSelector(selectExpandedNodeIds);
  const searchTerm = useSelector(selectAssetSearchTerm);
  const loading = useSelector(selectAssetLoading);
  const error = useSelector(selectAssetError);
  const addTemplateDrawerOpen = useSelector(selectAddTemplateDrawerOpen);
  const nodeActionDrawer = useSelector(selectNodeActionDrawer);
  const selectedSidebarItem = useSelector(selectSelectedSidebarItem);

  const linkableInventoryItems = useMemo(() => {
    return (siteInventories || []).filter(
      (inv: any) => inv && !inv.archivedAt && inv.assetItemId == null && inv.assetTemplateId == null
    );
  }, [siteInventories]);

  const didAutoSelectRef = useRef(false);
  const siteRef = useRef(site);
  const previousSiteKeyRef = useRef<string | null>(null);

  useEffect(() => {
    siteRef.current = site;
  }, [site]);

  useEffect(() => {
    if (!site) return;
    // Templates, groups and items are all site-scoped. Drop the previous site's
    // rows and selection before refetching, or the old template stays open and
    // the auto-select below picks from a list that belongs to the other site.
    const siteKey = `${site}|${JSON.stringify(multiSiteSelection ?? null)}`;
    if (previousSiteKeyRef.current !== null && previousSiteKeyRef.current !== siteKey) {
      dispatch(resetAssetView());
      didAutoSelectRef.current = false;
    }
    previousSiteKeyRef.current = siteKey;

    dispatch(fetchAssetTemplates());
    dispatch(fetchAssetGroups());
    dispatch(fetchAssetItems());
    dispatch(fetchAssetTaskOccurrences());
    dispatch(fetchAssetSiteInventories());
    // Populates the type dropdown in the shared Add Inventory modal, which this
    // page can open without the Inventory page ever having been visited.
    dispatch(fetchInventoryTypes());
  }, [dispatch, site, multiSiteSelection]);

  const selectedSidebarItemType = selectedSidebarItem?.itemType;
  const selectedSidebarItemId = selectedSidebarItem?.id;
  useEffect(() => {
    if (selectedSidebarItemId == null) return;
    if (selectedSidebarItemType === 'group') {
      dispatch(fetchAssetGroupDetail(selectedSidebarItemId));
    } else if (selectedSidebarItemType === 'item') {
      dispatch(fetchAssetItemDetail(selectedSidebarItemId));
    }
  }, [selectedSidebarItemType, selectedSidebarItemId, groups, items, dispatch]);

  const activeTemplates = useMemo(
    () => (templates || []).filter((t: any) => !t.archivedAt),
    [templates]
  );
  const activeGroups = useMemo(() => (groups || []).filter((g: any) => !g.archivedAt), [groups]);
  const activeItems = useMemo(() => (items || []).filter((i: any) => !i.archivedAt), [items]);

  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return activeTemplates;
    const lower = searchTerm.toLowerCase();
    return activeTemplates.filter(
      (t: any) => t.name?.toLowerCase().includes(lower) || t.type?.toLowerCase().includes(lower)
    );
  }, [activeTemplates, searchTerm]);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return activeGroups;
    const lower = searchTerm.toLowerCase();
    return activeGroups.filter((g: any) => g.name?.toLowerCase().includes(lower));
  }, [activeGroups, searchTerm]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return activeItems;
    const lower = searchTerm.toLowerCase();
    return activeItems.filter((i: any) => i.name?.toLowerCase().includes(lower));
  }, [activeItems, searchTerm]);

  const selectedNode = useMemo(() => {
    if (selectedSidebarItem) {
      const { itemType, data } = selectedSidebarItem;
      if (itemType === 'group') {
        return buildGroupNode(data, 0);
      }
      return buildItemNode(data);
    }
    return findNodeById(tree, selectedNodeId);
  }, [tree, selectedNodeId, selectedSidebarItem]);

  const breadcrumb = useMemo(() => {
    if (selectedSidebarItem) {
      const { itemType, data } = selectedSidebarItem;
      return [
        {
          id: `${itemType}-${data.id}`,
          name: data.name || `${itemType === 'group' ? 'Group' : 'Item'} #${data.id}`,
          type: itemType === 'group' ? 'group' : 'item',
        },
      ];
    }
    return getBreadcrumbPath(tree, selectedNodeId) || [];
  }, [tree, selectedNodeId, selectedSidebarItem]);

  const handleSelectTemplate = useCallback(
    async (templateId: number) => {
      const requestSite = site;
      dispatch({ type: `${GET_ASSET_TEMPLATE}_REQUEST` });
      try {
        const token = getJwtToken();
        const response = await AssetsAPI.getAssetTemplate({
          data: { id: templateId },
          options: {
            headers: { [TOKEN_HEADER]: token },
            siteId: requestSite,
          },
        });
        // A site switch while this was in flight would otherwise re-open the
        // template belonging to the site the user just left.
        if (siteRef.current !== requestSite) return;
        dispatch({ type: `${GET_ASSET_TEMPLATE}_SUCCESS`, response });
      } catch (err) {
        dispatch({ type: `${GET_ASSET_TEMPLATE}_FAILURE`, error: err });
      }
    },
    [dispatch, site]
  );

  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!activeTemplates || activeTemplates.length === 0) return;
    if (selectedTemplateId || selectedSidebarItem) {
      didAutoSelectRef.current = true;
      return;
    }
    const getTimestamp = (t: any): number => {
      const raw = t?.modifiedAt || t?.updatedAt || t?.createdAt;
      const parsed = raw ? new Date(raw).getTime() : NaN;
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const latest = [...activeTemplates].sort(
      (a: any, b: any) => getTimestamp(b) - getTimestamp(a)
    )[0];
    if (latest?.id) {
      didAutoSelectRef.current = true;
      handleSelectTemplate(latest.id);
    }
  }, [activeTemplates, selectedTemplateId, selectedSidebarItem, handleSelectTemplate]);

  const handleBackToTemplates = () => {
    dispatch(deselectTemplate());
    dispatch(deselectSidebarItem());
  };

  const handleSelectGroup = useCallback(
    (group: any) => {
      dispatch(deselectTemplate());
      dispatch(selectSidebarItem('group', group.id, group));
    },
    [dispatch]
  );

  const handleSelectItem = useCallback(
    (item: any) => {
      dispatch(deselectTemplate());
      dispatch(selectSidebarItem('item', item.id, item));
    },
    [dispatch]
  );

  const handleSelectSource = useCallback(
    (source: { id: number; kind: 'group' | 'item' } | null | undefined) => {
      if (!source?.id) return;
      if (source.kind === 'group') {
        const fresh = (groups || []).find((g: any) => g.id === source.id);
        if (fresh) handleSelectGroup(fresh);
      } else if (source.kind === 'item') {
        const fresh = (items || []).find((i: any) => i.id === source.id);
        if (fresh) handleSelectItem(fresh);
      }
    },
    [groups, items, handleSelectGroup, handleSelectItem]
  );

  const handleSelectNode = (nodeId: string) => {
    dispatch(selectAssetNode(nodeId));
  };

  const handleToggleNode = (nodeId: string) => {
    dispatch(toggleTreeNode(nodeId));
  };

  const handleSearchChange = (term: string) => {
    dispatch(setAssetSearchTerm(term));
  };

  const handleExpandAll = () => {
    dispatch(expandAllNodes());
  };

  const handleCollapseAll = () => {
    dispatch(collapseAllNodes());
  };

  const handleOpenAddTemplate = () => {
    dispatch(openAddTemplateDrawer());
  };

  const handleCloseAddTemplate = () => {
    dispatch(closeAddTemplateDrawer());
  };

  const handleAddTemplateSubmit = (data: any) => {
    dispatch({
      type: API_REQUEST,
      callAPI: AssetsAPI.postAssetTemplate,
      data: {
        name: data.name,
        type: data.type,
        version: data.version || 'v1',
        description: data.description || null,
      },
      action: 'CREATE_ASSET_TEMPLATE',
      callback: [() => closeAddTemplateDrawer(), () => fetchAssetTemplates()],
    });
  };

  const handleDuplicate = () => {
    if (selectedNode) dispatch(openNodeActionDrawer('duplicate', selectedNode, breadcrumb));
  };

  const handleMove = () => {
    if (selectedNode) dispatch(openNodeActionDrawer('move', selectedNode, breadcrumb));
  };

  const handleAddNew = () => {
    if (selectedNode) {
      dispatch(openNodeActionDrawer('add', selectedNode, breadcrumb));
    } else {
      dispatch(openAddTemplateDrawer());
    }
  };

  const handleAddGroup = () => {
    dispatch(
      openNodeActionDrawer(
        'addGroup',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb
      )
    );
  };

  const handleAddAsset = () => {
    dispatch(
      openNodeActionDrawer(
        'addAsset',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb
      )
    );
  };

  const handleAddDailyTask = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'dailyTask'
      )
    );
  };

  const handleAddPrevMaint = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'prevMaint'
      )
    );
  };

  const handleAddProject = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'project'
      )
    );
  };

  const handleAddInventory = () => {
    dispatch(
      (openNodeActionDrawer as any)(
        'add',
        selectedNode || { name: 'Root', type: 'template' },
        breadcrumb,
        'inventory'
      )
    );
  };

  const handleEditGroup = (group: any) => {
    const raw = group?.raw || group;
    if (!raw?.id) return;
    dispatch(
      (openNodeActionDrawer as any)(
        'editGroup',
        selectedNode || { name: getGroupDisplayName(raw), type: 'group' },
        breadcrumb,
        null,
        raw
      )
    );
  };

  const handleEditAsset = (item: any) => {
    const raw = item?.raw || item;
    if (!raw?.id) return;
    dispatch(
      (openNodeActionDrawer as any)(
        'editAsset',
        selectedNode || { name: getItemDisplayName(raw), type: 'item' },
        breadcrumb,
        null,
        raw
      )
    );
  };

  const handleEditTemplate = (template: any) => {
    const raw = template?.raw || template;
    if (!raw?.id) return;
    dispatch(
      (openNodeActionDrawer as any)(
        'editTemplate',
        selectedNode || { name: raw.name, type: 'template' },
        breadcrumb,
        null,
        raw
      )
    );
  };

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const selectedTemplateIdRef = useRef(selectedTemplateId);
  const selectedSidebarItemRef = useRef(selectedSidebarItem);
  useEffect(() => {
    selectedTemplateIdRef.current = selectedTemplateId;
  }, [selectedTemplateId]);
  useEffect(() => {
    selectedSidebarItemRef.current = selectedSidebarItem;
  }, [selectedSidebarItem]);

  const toArchiveTarget = (node: any): ArchiveTarget | null => {
    const type = node?.type;
    if (type !== 'template' && type !== 'group' && type !== 'item') return null;
    const id = node?.rawId ?? node?.raw?.id;
    if (!id) return null;
    return {
      type,
      id: Number(id),
      name: node?.name || '',
      childCount: countDescendants(node),
    };
  };

  const ARCHIVE_API = {
    template: AssetsAPI.archiveAssetTemplate,
    group: AssetsAPI.archiveAssetGroup,
    item: AssetsAPI.archiveAssetItem,
  };

  const UNARCHIVE_API = {
    template: AssetsAPI.unarchiveAssetTemplate,
    group: AssetsAPI.unarchiveAssetGroup,
    item: AssetsAPI.unarchiveAssetItem,
  };

  const refreshAfterArchiveChange = (target: ArchiveTarget) => {
    if (target.type === 'template') {
      dispatch(fetchAssetTemplates());
      return;
    }
    if (target.type === 'group') {
      dispatch(fetchAssetGroups());
    } else {
      dispatch(fetchAssetItems());
      dispatch(fetchAssetGroups());
    }
    const currentTemplateId = selectedTemplateIdRef.current;
    if (currentTemplateId) handleSelectTemplate(currentTemplateId);
  };

  const clearSelectionIfArchived = (target: ArchiveTarget) => {
    if (target.type === 'template') {
      if (selectedTemplateIdRef.current === target.id) dispatch(deselectTemplate());
      return;
    }
    const current = selectedSidebarItemRef.current;
    if (current?.itemType === target.type && current?.id === target.id) {
      dispatch(deselectSidebarItem());
    }
  };

  const handleArchiveNode = (node: any) => {
    const target = toArchiveTarget(node);
    if (target) {
      setArchiveError(null);
      setArchiveTarget(target);
    }
  };

  const handleConfirmArchive = () => {
    const target = archiveTarget;
    if (!target) return;
    setArchiveSubmitting(true);
    setArchiveError(null);
    dispatch({
      type: API_REQUEST,
      callAPI: ARCHIVE_API[target.type],
      data: { id: target.id },
      options: { siteId: site },
      action: 'ARCHIVE_ASSET_NODE',
      voidCallback: () => {
        setArchiveSubmitting(false);
        setArchiveTarget(null);
        clearSelectionIfArchived(target);
        refreshAfterArchiveChange(target);
      },
      errorCallback: (err: any) => {
        setArchiveSubmitting(false);
        setArchiveError(err?.data || err?.message || 'archiveFailed');
      },
    });
  };

  const handleUnarchiveNode = (node: any) => {
    const target = toArchiveTarget(node);
    if (!target) return;
    dispatch({
      type: API_REQUEST,
      callAPI: UNARCHIVE_API[target.type],
      data: { id: target.id },
      options: { siteId: site },
      action: 'UNARCHIVE_ASSET_NODE',
      voidCallback: () => refreshAfterArchiveChange(target),
    });
  };

  const handleArchiveTask = (taskItem: any) => {
    const rawId =
      taskItem?.raw?.id || (taskItem?.id ? String(taskItem.id).replace('task-', '') : null);
    if (!rawId) return;
    dispatch({
      type: API_REQUEST,
      callAPI: AssetsAPI.archiveTaskOccurrence,
      data: { id: Number(rawId) },
      options: { siteId: site },
      action: 'ARCHIVE_TASK_INSTANCE',
      voidCallback: () => {
        dispatch(fetchAssetGroups());
        dispatch(fetchAssetItems());
      },
    });
  };

  const handleUnlinkInventory = (inventoryItem: any) => {
    const rawId =
      inventoryItem?.raw?.id ||
      (inventoryItem?.id ? String(inventoryItem.id).replace('inv-', '') : null);
    if (!rawId) return;
    dispatch({
      type: API_REQUEST,
      callAPI: AssetsAPI.patchInventory,
      data: { id: Number(rawId), assetItemId: null },
      options: { siteId: site },
      action: 'UNLINK_INVENTORY_FROM_ASSET',
      voidCallback: () => {
        dispatch(fetchAssetGroups());
        dispatch(fetchAssetItems());
        dispatch(fetchAssetSiteInventories());
      },
    });
  };

  const handleLinkDrop = useCallback(
    async (
      targetType: 'template' | 'group',
      targetId: number,
      dragType: 'group' | 'item',
      dragId: number
    ) => {
      try {
        const token = getJwtToken();
        const opts = { headers: { [TOKEN_HEADER]: token }, siteId: site };

        if (targetType === 'template' && dragType === 'group') {
          await AssetsAPI.linkTemplateGroup({
            data: { id: targetId, assetGroupId: dragId },
            options: opts,
          });
          dispatch(fetchAssetTemplates());
          dispatch(fetchAssetGroups());
        } else if (targetType === 'template' && dragType === 'item') {
          await AssetsAPI.linkTemplateItem({
            data: { id: targetId, assetItemId: dragId },
            options: opts,
          });
          dispatch(fetchAssetTemplates());
          dispatch(fetchAssetItems());
        } else if (targetType === 'group' && dragType === 'group') {
          if (targetId === dragId) return;
          await AssetsAPI.addGroupChild({
            data: { id: targetId, childGroupId: dragId },
            options: opts,
          });
          dispatch(fetchAssetGroups());
        } else if (targetType === 'group' && dragType === 'item') {
          await AssetsAPI.linkGroupItem({
            data: { id: targetId, assetItemId: dragId },
            options: opts,
          });
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        }

        const openTemplateId = selectedTemplateIdRef.current;
        if (openTemplateId) handleSelectTemplate(openTemplateId);
      } catch {
        // noop
      }
    },
    [dispatch, site, handleSelectTemplate]
  );

  const handleCloseNodeDrawer = () => {
    dispatch(closeNodeActionDrawer());
  };

  const isGroupOrAssetContext = !!selectedSidebarItem;

  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [taskDrawerAssetScope, setTaskDrawerAssetScope] = useState<AssetScope | undefined>(
    undefined
  );
  const [selectedOccurrence, setSelectedOccurrence] = useState<TaskOccurrence | null>(null);
  const [occurrenceDrawerError, setOccurrenceDrawerError] = useState<string | null>(null);
  const [updateOccurrence, { isLoading: isSavingOccurrenceDrawer }] = useUpdateOccurrenceMutation();

  const handleOpenOccurrence = (occurrence: TaskOccurrence): void => {
    setOccurrenceDrawerError(null);
    setSelectedOccurrence(occurrence);
  };

  const handleCloseOccurrenceDrawer = (): void => {
    setSelectedOccurrence(null);
    setOccurrenceDrawerError(null);
  };

  const handleOccurrenceDrawerSave = async (patch: Partial<TaskOccurrence>): Promise<void> => {
    if (!selectedOccurrence) return;
    setOccurrenceDrawerError(null);
    try {
      await updateOccurrence({
        occurrenceId: selectedOccurrence.id,
        notes: (patch.notes as any) ?? undefined,
        scheduleStart: (patch.scheduleStart as any) ?? undefined,
        scheduleEnd: (patch.scheduleEnd as any) ?? undefined,
        employeeId: (patch.employeeId as any) ?? undefined,
      }).unwrap();
      setSelectedOccurrence(null);
      dispatch(fetchAssetGroups());
      dispatch(fetchAssetItems());
      dispatch(fetchAssetTaskOccurrences());
    } catch (e: any) {
      setOccurrenceDrawerError(e?.data || 'Failed to save occurrence');
    }
  };
  const [taskDrawerAddDefaults, setTaskDrawerAddDefaults] = useState<
    Partial<DrawerFormState> | undefined
  >(undefined);
  const [taskDrawerTitle, setTaskDrawerTitle] = useState<string | undefined>(undefined);

  const refreshAssetsAfterTaskCreate = () => {
    dispatch(fetchAssetGroups());
    dispatch(fetchAssetItems());
    dispatch(fetchAssetTaskOccurrences());
    if (selectedTemplateId) handleSelectTemplate(selectedTemplateId);
    return undefined;
  };

  const handleNodeActionSubmit = (mode: string, itemType: string | null, data: any) => {
    const siteId = site;

    if (mode === 'add' && data && data.__link) {
      const drawerNode = nodeActionDrawer?.node;
      let scopeType: 'group' | 'item' | 'template' | null = null;
      let scopeId: number | null = null;
      if (selectedSidebarItem) {
        scopeType = selectedSidebarItem.itemType as 'group' | 'item';
        scopeId = selectedSidebarItem.id;
      } else if (
        drawerNode?.type === 'group' ||
        drawerNode?.type === 'item' ||
        drawerNode?.type === 'template'
      ) {
        scopeType = drawerNode.type;
        scopeId = drawerNode.rawId ?? drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
      }
      const occurrenceId = Number(data.linkOccurrenceId);
      if (!scopeType || !scopeId || !Number.isFinite(occurrenceId) || occurrenceId <= 0) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const linkToAsset = async () => {
        try {
          const token = getJwtToken();
          const opts = { headers: { [TOKEN_HEADER]: token }, siteId };
          const patchBody: any = { id: occurrenceId };
          if (scopeType === 'group') patchBody.assetGroupId = scopeId;
          else if (scopeType === 'template') patchBody.assetTemplateId = scopeId;
          else patchBody.assetItemId = scopeId;
          await AssetsAPI.patchTaskOccurrence({
            data: patchBody,
            options: opts,
          });
        } finally {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
          dispatch(fetchAssetTaskOccurrences());
          if (selectedTemplateId) handleSelectTemplate(selectedTemplateId);
        }
      };
      linkToAsset();
      return;
    }

    if (mode === 'add' && data && data.__linkInventory) {
      const drawerNode = nodeActionDrawer?.node;
      const inventoryId = Number(data.linkInventoryId);
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        dispatch(closeNodeActionDrawer());
        return;
      }

      const refreshAfterInventoryChange = () => {
        dispatch(closeNodeActionDrawer());
        dispatch(fetchAssetGroups());
        dispatch(fetchAssetItems());
        dispatch(fetchAssetSiteInventories());
        const openTemplateId = selectedTemplateIdRef.current;
        if (openTemplateId) handleSelectTemplate(openTemplateId);
      };

      const scopeTemplateId = inventoryScopeTemplateId(drawerNode);
      if (scopeTemplateId) {
        dispatch({
          type: API_REQUEST,
          callAPI: AssetsAPI.linkTemplateInventory,
          data: { assetTemplateId: scopeTemplateId, inventoryId },
          options: { siteId },
          action: 'LINK_INVENTORY_TO_TEMPLATE',
          voidCallback: refreshAfterInventoryChange,
        });
        return;
      }

      let scopeItemId: number | null = null;
      if (selectedSidebarItem?.itemType === 'item') {
        scopeItemId = selectedSidebarItem.id;
      } else if (drawerNode?.type === 'item') {
        scopeItemId = drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
      }
      if (!scopeItemId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchInventory,
        data: { id: inventoryId, assetItemId: scopeItemId },
        options: { siteId },
        action: 'LINK_INVENTORY_TO_ASSET',
        voidCallback: refreshAfterInventoryChange,
      });
      return;
    }

    if (mode === 'add' && data && data.__openInventoryEditor) {
      const drawerNode = nodeActionDrawer?.node;
      const scopeTemplateId = inventoryScopeTemplateId(drawerNode);
      let scopeItemId: number | null = null;
      if (!scopeTemplateId) {
        if (selectedSidebarItem?.itemType === 'item') {
          scopeItemId = selectedSidebarItem.id;
        } else if (drawerNode?.type === 'item') {
          scopeItemId = drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
        }
      }
      dispatch(closeNodeActionDrawer());
      if (!scopeTemplateId && !scopeItemId) return;
      dispatch(
        showDialog('UPSERT_INVENTORY', {
          isNew: true,
          intl,
          title: intl.formatMessage({ id: 'addInventoryItem' }),
          onSubmit: ({ item }: any) =>
            dispatch(
              addInventoryItem({
                item: {
                  ...item,
                  ...(scopeTemplateId
                    ? { assetTemplateId: scopeTemplateId }
                    : { assetItemId: scopeItemId }),
                },
                callback: [
                  () => fetchAssetGroups(),
                  () => fetchAssetItems(),
                  () => fetchAssetSiteInventories(),
                ],
                voidCallback: scopeTemplateId
                  ? () => handleSelectTemplate(scopeTemplateId)
                  : undefined,
              })
            ),
        })
      );
      return;
    }

    if (mode === 'add' && data && data.__openTaskEditor) {
      const drawerNode = nodeActionDrawer?.node;
      let scopeType: 'group' | 'item' | 'template' | null = null;
      let scopeId: number | null = null;
      let scopeName = '';
      if (selectedSidebarItem) {
        scopeType = selectedSidebarItem.itemType as 'group' | 'item';
        scopeId = selectedSidebarItem.id;
        scopeName =
          selectedSidebarItem.itemType === 'group'
            ? getGroupDisplayName(selectedSidebarItem.data)
            : getItemDisplayName(selectedSidebarItem.data);
      } else if (
        drawerNode?.type === 'group' ||
        drawerNode?.type === 'item' ||
        drawerNode?.type === 'template'
      ) {
        scopeType = drawerNode.type;
        scopeId = drawerNode.rawId ?? drawerNode.raw?.id ?? drawerNode.data?.id ?? null;
        scopeName = drawerNode.name || '';
      }
      if (!scopeType || !scopeId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const typeByCard: Record<string, string> = {
        dailyTask: 'CleaningList',
        prevMaint: 'PreventativeMaintenance',
        project: 'Project',
      };
      const addDefaults: Partial<DrawerFormState> = {
        type: typeByCard[itemType || 'dailyTask'] || 'CleaningList',
      };
      if (itemType === 'project') addDefaults.interval = 'Once';
      const scope: AssetScope =
        scopeType === 'group'
          ? { assetGroupId: scopeId }
          : scopeType === 'template'
          ? { assetTemplateId: scopeId }
          : { assetItemId: scopeId };
      dispatch(closeNodeActionDrawer());
      setTaskDrawerAssetScope(scope);
      setTaskDrawerAddDefaults(addDefaults);
      setTaskDrawerTitle(
        intl.formatMessage({ id: 'assets.drawer.addTo' }, { nodeName: scopeName || '' })
      );
      setTaskDrawerOpen(true);
      return;
    }

    if (mode === 'add' && itemType === 'dailyTask' && isGroupOrAssetContext) {
      const instanceData: any = {
        name: data.name,
        type: 'daily',
        status: 0,
      };
      if (selectedSidebarItem.itemType === 'group') {
        instanceData.assetGroupId = selectedSidebarItem.id;
      } else if (selectedSidebarItem.itemType === 'item') {
        instanceData.assetItemId = selectedSidebarItem.id;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postTaskOccurrence,
        data: instanceData,
        options: { siteId },
        action: 'CREATE_TASK_INSTANCE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        },
      });
    } else if (mode === 'add' && itemType === 'prevMaint' && isGroupOrAssetContext) {
      const instanceData: any = {
        name: data.name,
        type: 'preventive_maintenance',
        priority:
          data.priority?.toLowerCase() === 'high'
            ? 3
            : data.priority?.toLowerCase() === 'critical'
            ? 4
            : data.priority?.toLowerCase() === 'low'
            ? 1
            : 2,
        status: 0,
      };
      if (selectedSidebarItem.itemType === 'group') {
        instanceData.assetGroupId = selectedSidebarItem.id;
      } else if (selectedSidebarItem.itemType === 'item') {
        instanceData.assetItemId = selectedSidebarItem.id;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postTaskOccurrence,
        data: instanceData,
        options: { siteId },
        action: 'CREATE_TASK_INSTANCE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        },
      });
    } else if (mode === 'add' && itemType === 'project' && isGroupOrAssetContext) {
      const instanceData: any = {
        name: data.name,
        type: 'project',
        status: 0,
      };
      if (selectedSidebarItem.itemType === 'group') {
        instanceData.assetGroupId = selectedSidebarItem.id;
      } else if (selectedSidebarItem.itemType === 'item') {
        instanceData.assetItemId = selectedSidebarItem.id;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postTaskOccurrence,
        data: instanceData,
        options: { siteId },
        action: 'CREATE_TASK_INSTANCE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        },
      });
    } else if (mode === 'add' && itemType === 'inventory') {
      dispatch(closeNodeActionDrawer());
    } else if (mode === 'addGroup') {
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postAssetGroup,
        data: {
          name: data.name,
          notes: data.description || null,
        },
        options: { siteId },
        action: 'CREATE_ASSET_GROUP',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          if (selectedTemplateId) handleSelectTemplate(selectedTemplateId);
        },
      });
    } else if (mode === 'addAsset') {
      const itemData: any = {
        name: data.name,
        notes: data.notes || null,
      };
      if (data.assetGroupId) {
        itemData.assetGroupId = Number(data.assetGroupId);
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.postAssetItem,
        data: itemData,
        options: { siteId },
        action: 'CREATE_ASSET_ITEM',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetTemplates());
          dispatch(fetchAssetItems());
          if (selectedTemplateId) handleSelectTemplate(selectedTemplateId);
        },
      });
    } else if (mode === 'editTask') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.raw?.id || (ed?.id ? String(ed.id).replace('task-', '') : null);
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const patchData: any = {
        id: Number(rawId),
        name: data.name,
        status: data.status != null ? parseInt(data.status, 10) : undefined,
        instructions: data.description || null,
      };
      const rawType = (ed?.raw?.type || '').toLowerCase();
      if (rawType === 'preventive_maintenance' && data.priority) {
        const priorityKey = String(data.priority).toLowerCase();
        patchData.priority =
          priorityKey === 'critical'
            ? 4
            : priorityKey === 'high'
            ? 3
            : priorityKey === 'low'
            ? 1
            : 2;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchTaskOccurrence,
        data: patchData,
        options: { siteId },
        action: 'UPDATE_TASK_INSTANCE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          dispatch(fetchAssetItems());
        },
      });
    } else if (mode === 'editTemplate') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.id;
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchAssetTemplate,
        data: {
          id: Number(rawId),
          name: data.name,
          type: data.type,
          version: data.version || 'v1',
          description: data.description || null,
        },
        options: { siteId },
        action: 'UPDATE_ASSET_TEMPLATE',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetTemplates());
          handleSelectTemplate(Number(rawId));
        },
      });
    } else if (mode === 'editGroup') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.id;
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchAssetGroup,
        data: {
          id: Number(rawId),
          name: data.name,
          notes: data.description || null,
        },
        options: { siteId },
        action: 'UPDATE_ASSET_GROUP',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetGroups());
          if (selectedTemplateId) handleSelectTemplate(selectedTemplateId);
        },
      });
    } else if (mode === 'editAsset') {
      const ed = nodeActionDrawer.editData;
      const rawId = ed?.id;
      if (!rawId) {
        dispatch(closeNodeActionDrawer());
        return;
      }
      const patchData: any = {
        id: Number(rawId),
        name: data.name,
        notes: data.notes || null,
      };
      if (data.assetGroupId) {
        patchData.assetGroupId = Number(data.assetGroupId);
      }
      dispatch({
        type: API_REQUEST,
        callAPI: AssetsAPI.patchAssetItem,
        data: patchData,
        options: { siteId },
        action: 'UPDATE_ASSET_ITEM',
        voidCallback: () => {
          dispatch(closeNodeActionDrawer());
          dispatch(fetchAssetItems());
          dispatch(fetchAssetGroups());
          if (selectedTemplateId) handleSelectTemplate(selectedTemplateId);
        },
      });
    } else {
      dispatch(closeNodeActionDrawer());
    }
  };

  return (
    <div className="assets-page">
      <div className="assets-page__sidebar">
        <AssetTree
          templates={filteredTemplates}
          groups={filteredGroups}
          items={filteredItems}
          tree={tree}
          selectedNodeId={selectedNodeId}
          selectedTemplateId={selectedTemplateId}
          selectedSidebarItem={selectedSidebarItem}
          expandedNodeIds={expandedNodeIds}
          searchTerm={searchTerm}
          onSelectNode={handleSelectNode}
          onToggleNode={handleToggleNode}
          onSearchChange={handleSearchChange}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          onAddTemplate={handleOpenAddTemplate}
          onSelectTemplate={handleSelectTemplate}
          onSelectGroup={handleSelectGroup}
          onSelectItem={handleSelectItem}
          onBackToTemplates={handleBackToTemplates}
          onLinkDrop={handleLinkDrop}
        />
      </div>

      <div className="assets-page__content">
        {loading && (
          <div className="assets-page__loading">
            <i className="uil uil-spinner-alt uil-spin" />
            <p>Loading template...</p>
          </div>
        )}
        {error && (
          <div className="assets-page__error">
            <i className="uil uil-exclamation-triangle" />
            <p>Failed to load template: {String(error)}</p>
          </div>
        )}
        {!loading && (
          <AssetDetail
            node={selectedNode}
            breadcrumb={breadcrumb}
            templateName={selectedSidebarItem ? selectedNode?.name || '' : tree?.name || ''}
            onSelectNode={handleSelectNode}
            onDuplicate={handleDuplicate}
            onMove={handleMove}
            onAddNew={handleAddNew}
            onAddTemplate={handleOpenAddTemplate}
            onAddGroup={handleAddGroup}
            onAddAsset={handleAddAsset}
            onAddDailyTask={handleAddDailyTask}
            onAddPrevMaint={handleAddPrevMaint}
            onAddProject={handleAddProject}
            onAddInventory={handleAddInventory}
            onArchiveTask={handleArchiveTask}
            onOpenOccurrence={handleOpenOccurrence}
            onUnlinkInventory={handleUnlinkInventory}
            onEditGroup={handleEditGroup}
            onEditAsset={handleEditAsset}
            onEditTemplate={handleEditTemplate}
            onArchiveNode={handleArchiveNode}
            onUnarchiveNode={handleUnarchiveNode}
            onSelectSource={handleSelectSource}
          />
        )}
      </div>

      <ArchiveConfirmModal
        target={archiveTarget}
        isSubmitting={archiveSubmitting}
        error={archiveError}
        onConfirm={handleConfirmArchive}
        onCancel={() => {
          setArchiveTarget(null);
          setArchiveError(null);
        }}
      />

      {addTemplateDrawerOpen && (
        <AddTemplateDrawer
          isOpen
          onClose={handleCloseAddTemplate}
          onSubmit={handleAddTemplateSubmit}
        />
      )}

      {nodeActionDrawer && nodeActionDrawer.open && (
        <AddToNodeDrawer
          isOpen
          mode={nodeActionDrawer.mode}
          node={nodeActionDrawer.node}
          breadcrumb={nodeActionDrawer.breadcrumb}
          groups={activeGroups}
          taskOccurrences={linkableTaskOccurrences}
          inventoryItems={linkableInventoryItems}
          isAssetContext={isGroupOrAssetContext}
          initialItemType={nodeActionDrawer.initialItemType}
          editData={nodeActionDrawer.editData}
          onClose={handleCloseNodeDrawer}
          onSubmit={handleNodeActionSubmit}
        />
      )}

      <TaskTemplateAdminDrawer
        isOpen={taskDrawerOpen}
        mode={taskDrawerOpen ? 'add' : null}
        addDefaults={taskDrawerAddDefaults}
        assetScope={taskDrawerAssetScope}
        titleOverride={taskDrawerTitle}
        onClose={() => {
          setTaskDrawerOpen(false);
          setTaskDrawerAssetScope(undefined);
          setTaskDrawerAddDefaults(undefined);
          setTaskDrawerTitle(undefined);
        }}
        onSaved={(saved) => {
          const scope = taskDrawerAssetScope;
          const materializeOccurrence = async () => {
            try {
              if (!saved?.id || !site) return;
              const token = getJwtToken();
              const opts = { headers: { [TOKEN_HEADER]: token }, siteId: site };
              const ensureResult: any = await AssetsAPI.postEnsureTaskOccurrence({
                data: { taskId: saved.id },
                options: opts,
              });
              const occurrenceId: number | undefined = ensureResult?.occurrence?.id;
              const wantsAssetPatch =
                occurrenceId != null &&
                (scope?.assetGroupId != null ||
                  scope?.assetItemId != null ||
                  scope?.assetTemplateId != null);
              if (wantsAssetPatch) {
                const patchBody: any = { id: occurrenceId };
                if (scope?.assetGroupId != null) patchBody.assetGroupId = scope.assetGroupId;
                if (scope?.assetItemId != null) patchBody.assetItemId = scope.assetItemId;
                if (scope?.assetTemplateId != null) {
                  patchBody.assetTemplateId = scope.assetTemplateId;
                }
                await AssetsAPI.patchTaskOccurrence({ data: patchBody, options: opts });
              }
            } catch {
              // scheduler will materialize the occurrence on its next tick if this fails
            } finally {
              refreshAssetsAfterTaskCreate();
            }
          };
          materializeOccurrence();
        }}
      />

      <Drawer
        expanded={!!selectedOccurrence}
        maximized={false}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        onBackdropClick={handleCloseOccurrenceDrawer}
      >
        {selectedOccurrence && (
          <OccurrenceDrawerBody
            occurrence={selectedOccurrence}
            onClose={handleCloseOccurrenceDrawer}
            onSave={handleOccurrenceDrawerSave}
            isSaving={isSavingOccurrenceDrawer}
            error={occurrenceDrawerError}
          />
        )}
      </Drawer>
    </div>
  );
};

export default AssetsContainer;
